import moment from "moment";
import * as d3 from "d3";

const capita = 10000;
const year = 2021;
const parse_time = d3.timeParse("%m/%d/%Y %I:%M:%S %p");

const colorValues = [
  "#542788",
  "#8073ac",
  "#b2abd2",
  "#d8daeb",
  "#f7f7f7",
  "#fee0b6",
  "#fdb863",
  "#e08214",
  "#b35806",
];

const filterBy = {
  victimizations: (d) =>
    d.GUNSHOT_INJURY_I === "YES" || d.VICTIMIZATION_PRIMARY === "HOMICIDE",
  homicides: (d) => d.VICTIMIZATION_PRIMARY === "HOMICIDE",
  nonFatalShootings: (d) =>
    d.GUNSHOT_INJURY_I === "YES" && d.VICTIMIZATION_PRIMARY !== "HOMICIDE",
};

const rankArr = [-15, -10, -5, -1, 1, 5, 10, 15];
const color = d3.scaleThreshold().domain(rankArr).range(colorValues);

const parseMDY = d3.timeParse("%m/%d/%Y");
const getCurrentYear = (d, updated) => {
  const current = d.filter(
    (o) =>
      parse_time(o.DATE) >= parseMDY(`01/01/${year}`) &&
      parse_time(o.DATE) <= parse_time(updated)
  );
  return current;
};

const getPreviousYear = (d, updated) => {
  const month_day = d3.timeFormat("%m/%d")(parse_time(updated));
  const previous = d.filter(
    (o) =>
      parse_time(o.DATE) >= parseMDY(`01/01/${year - 1}`) &&
      parse_time(o.DATE) <= parseMDY(`${month_day}/${year - 1}`)
  );
  return previous;
};

const calculateRank = (d, updated, population) => {
  const currentYear = (getCurrentYear(d, updated).length / population) * capita;
  const previousYear =
    (getPreviousYear(d, updated).length / population) * capita;
  return currentYear - previousYear;
};

const getMaxPerCapita = (data, updated) => {
  const current = data.map((d) => {
    return (
      (getCurrentYear(d[1].victimizations, updated).length / d[1].population) *
      capita
    );
  });
  const previous = data.map((d) => {
    return (
      (getPreviousYear(d[1].victimizations, updated).length / d[1].population) *
      capita
    );
  });
  const maxCurrent = d3.max(current, (d) => d);
  const maxPrevious = d3.max(previous, (d) => d);
  return maxCurrent > maxPrevious ? maxCurrent : maxPrevious;
};

const calculateTotal = (data, updated, population = null) => {
  const endDate = moment(updated).add(1, "days").format("M/D/YYYY 12:00:00 a");
  const t = data.filter(
    (d) =>
      parse_time(d.DATE) >= parse_time(`01/01/${year} 12:00:00 pm`) &&
      parse_time(d.DATE) < parse_time(endDate)
  ).length;
  return population ? (t / population) * capita : t;
};

const calculate3YearAvg = (data, updated, population = null) => {
  const threeYearsAgo = moment(updated)
    .subtract(3, "year")
    .format("M/D/YYYY 12:00:00 a");
  const twoYearsAgo = moment(updated)
    .subtract(2, "year")
    .format("M/D/YYYY 12:00:00 a");
  const oneYearAgo = moment(updated)
    .subtract(1, "year")
    .format("M/D/YYYY 12:00:00 a");
  const tomorrow = moment(updated).add(1, "days").format("M/D/YYYY 12:00:00 a");
  const y1 = data.filter(
    (d) =>
      parse_time(d.DATE) >= parse_time(threeYearsAgo) &&
      parse_time(d.DATE) < parse_time(twoYearsAgo)
  ).length;
  const y2 = data.filter(
    (d) =>
      parse_time(d.DATE) >= parse_time(twoYearsAgo) &&
      parse_time(d.DATE) < parse_time(oneYearAgo)
  ).length;
  const y3 = data.filter(
    (d) =>
      parse_time(d.DATE) >= parse_time(oneYearAgo) &&
      parse_time(d.DATE) < parse_time(tomorrow)
  ).length;
  if (population) {
    const y1_perCapita = (y1 / population) * capita;
    const y2_perCapita = (y2 / population) * capita;
    const y3_perCapita = (y3 / population) * capita;
    return (y1_perCapita + y2_perCapita + y3_perCapita) / 3;
  } else {
    return (y1 + y2 + y3) / 3;
  }
};

const calculate18to20Avg = (data, population) => {
  const year2018 = data.filter(
    (d) => parse_time(d.DATE).getFullYear() === 2018
  ).length;
  const year2019 = data.filter(
    (d) => parse_time(d.DATE).getFullYear() === 2019
  ).length;
  const year2020 = data.filter(
    (d) => parse_time(d.DATE).getFullYear() === 2020
  ).length;
  if (population) {
    const year2018_perCapita = (year2018 / population) * capita;
    const year2019_perCapita = (year2019 / population) * capita;
    const year2020_perCapita = (year2020 / population) * capita;
    return (year2018_perCapita + year2019_perCapita + year2020_perCapita) / 3;
  } else {
    return (year2018 + year2019 + year2020) / 3;
  }
};

export {
  year,
  parse_time,
  getMaxPerCapita,
  color,
  colorValues,
  rankArr,
  calculateRank,
  calculateTotal,
  calculate3YearAvg,
  calculate18to20Avg,
  filterBy,
  getCurrentYear,
  getPreviousYear,
};
